import styled from '@emotion/styled';
import React, { useRef } from 'react';
import { breakpoints, colors, spacing } from '../../../../utils/styleguide';
import { sendGTMEvent } from '../../../../utils/tracking/gtm/sendGTMEvent';
import Icon from '../../Icon';
import ResponsiveImage from '../../ResponsiveImage';
import Typography from '../../text/Typography';
import { ImpactersSectionImpacter } from './ImpacterListLayout';
import useLazyLoadedSources from './useLazyLoadedSources';

type CardContainerProps = {
  desktopCardWidth: number;
  mobileCardWidth: number;
};

const CardContainer = styled.a`
  display: block;
  min-width: ${(p: CardContainerProps) => p.mobileCardWidth}px;
  max-width: ${(p: CardContainerProps) => p.mobileCardWidth}px;
  margin: 0 ${spacing[2]}px;
  box-sizing: border-box;
  box-shadow: 2px 8px 16px rgba(10, 29, 82, 0.12);
  overflow: hidden;
  text-decoration: none;
  ${breakpoints.tablet} {
    margin: 0 ${spacing[3]}px;
    min-width: ${(p: CardContainerProps) => p.desktopCardWidth}px;
    max-width: ${(p: CardContainerProps) => p.desktopCardWidth}px;
  }
`;

type CardProps = {
  desktopCardWidth: number;
  mobileCardWidth: number;
};

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: ${(p: CardProps) => p.mobileCardWidth}px;
  height: ${(p: CardProps) => p.mobileCardWidth * 1.2}px; // 4:5
  background: ${colors.black};
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  ${breakpoints.tablet} {
    max-width: ${(p: CardProps) => p.desktopCardWidth}px;
    height: ${(p: CardProps) => p.desktopCardWidth}px;
  }
`;

const CardImage = styled(ResponsiveImage)`
  position: absolute;
  background: ${colors.black};
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 60%, rgba(0, 0, 0, 0.9) 100%);
`;

const BelowCardText = styled.div`
  margin: ${spacing[3]}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ImpacterProfileImage = styled(ResponsiveImage)`
  width: 48px;
  height: 48px;
  object-fit: cover;
  z-index: 1;
  border-radius: 50%;
  background-color: ${colors.black};
  color: ${colors.white};
  margin-right: ${spacing[2]}px;
  ${breakpoints.tablet} {
    width: 60px;
    height: 60px;
  }
`;

const ImpacterProfileInfo = styled.div`
  flex: 1;
  min-width: 50%;
  height: 60px;
  overflow-wrap: break-word;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${breakpoints.tablet} {
    height: auto;
  }
`;

const ImpacterInfoBlock = styled(Typography)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${breakpoints.tablet} {
    max-width: 250px;
    overflow-wrap: break-word;
  }
`;

const MarkdownTaglineText = styled(Typography)`
  margin: ${spacing[4]}px;
  z-index: 1;
`;

const SnapContainer = styled.div`
  scroll-snap-align: start;
  scroll-margin-left: ${spacing[2]}px;
  ${breakpoints.tablet} {
    scroll-margin-left: ${spacing[3]}px;
  }
`;

type MarkdownTaglineProps = {
  children?: string;
};

const MarkdownTagline = ({ children = '' }: MarkdownTaglineProps) => {
  const words = children.split('**');
  return (
    <MarkdownTaglineText useBuiltInMargins variant="smallTitle" color={colors.yellow}>
      {words.map((word, index) => {
        if (index % 2 === 1) {
          return (
            <Typography
              useBuiltInMargins
              key={index}
              tag="span"
              variant="smallTitleHand"
              color={colors.yellow}
            >
              {word}
            </Typography>
          );
        } else {
          return word;
        }
      })}
    </MarkdownTaglineText>
  );
};

type ImpacterCardProps = {
  id: number;
  name?: string;
  organisationName?: string;
  catchPhrase?: string;
  location?: string;
  desktopCardWidth: number;
  mobileCardWidth: number;
};

const ImpacterCard = ({
  id,
  name,
  organisationName,
  catchPhrase,
  mobileCardWidth,
  desktopCardWidth,
}: ImpacterCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const impacterSources = useLazyLoadedSources({ id, ref });
  const backgroundSources = impacterSources?.actionShotWideSources;
  const headShotSources = impacterSources?.headShotSources;
  return (
    <SnapContainer ref={ref}>
      <CardContainer
        onClick={() =>
          sendGTMEvent('productClick', {
            donationItemType: 'Impacter',
            items: [{ id, name, category: organisationName }],
          })
        }
        href={`/impacters/${id}`}
        target="_blank"
        rel="noopener noreferrer"
        desktopCardWidth={desktopCardWidth}
        mobileCardWidth={mobileCardWidth}
      >
        <Card desktopCardWidth={desktopCardWidth} mobileCardWidth={mobileCardWidth}>
          {backgroundSources?.mobile?.[1] && (
            <CardImage sources={backgroundSources} loading="lazy" alt="Impacter picture" />
          )}
          <Overlay />
          <MarkdownTagline>{catchPhrase}</MarkdownTagline>
        </Card>
        <BelowCardText>
          <ImpacterProfileImage sources={headShotSources} alt={`${name} picture`} loading="lazy" />
          <ImpacterProfileInfo>
            <ImpacterInfoBlock variant="bodyBold">{name}</ImpacterInfoBlock>
            <ImpacterInfoBlock variant="detail" color={colors.darkGrey}>
              {organisationName}
            </ImpacterInfoBlock>
          </ImpacterProfileInfo>
          <Icon stroke={colors.black} name="arrow-right" size={21} />
        </BelowCardText>
      </CardContainer>
    </SnapContainer>
  );
};

const OuterWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  overflow-x: scroll;
  overflow-y: hidden;
  padding: 0 ${spacing[1]}px ${spacing[4]}px;
  ::-webkit-scrollbar {
    display: none;
  }
  ${breakpoints.tablet} {
    padding: 0 ${spacing[7]}px ${spacing[6]}px;
  }
`;

interface CarouselSliderProps {
  impacters: ImpactersSectionImpacter[];
  onScroll: React.EventHandler<React.UIEvent<HTMLElement>>;
  desktopCardWidth: number;
  mobileCardWidth: number;
}

const CarouselSlider = React.forwardRef<HTMLDivElement, CarouselSliderProps>(
  function CarouselSlider({ impacters, onScroll, desktopCardWidth, mobileCardWidth }, ref) {
    return (
      <OuterWrapper ref={ref} onScroll={onScroll}>
        {impacters.map(({ id, name, organisationName, tagline, location }) => (
          <ImpacterCard
            key={id}
            id={id}
            name={name}
            organisationName={organisationName}
            catchPhrase={tagline?.[0].content}
            location={location}
            desktopCardWidth={desktopCardWidth}
            mobileCardWidth={mobileCardWidth}
          />
        ))}
      </OuterWrapper>
    );
  },
);

export default CarouselSlider;
